/**
 * Lightweight, browser-only wishlist.
 *
 * Saves product handles to localStorage (per-browser, no account needed) and
 * keeps every heart button, the header wishlist bubble, and the wishlist
 * page grid in sync via a single `wishlist:change` document event -- no
 * page reload required when a heart is toggled anywhere on the site.
 */
(function () {
  const STORAGE_KEY = 'wurvle:wishlist';

  function readList() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeList(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      /* Storage unavailable (private browsing, quota) -- fail silently. */
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles: list } }));
  }

  const Wishlist = {
    getAll() {
      return readList();
    },
    has(handle) {
      return readList().includes(handle);
    },
    add(handle) {
      if (!handle) return;
      const list = readList();
      if (!list.includes(handle)) {
        list.push(handle);
        writeList(list);
      }
    },
    remove(handle) {
      if (!handle) return;
      writeList(readList().filter((item) => item !== handle));
    },
    /** @returns {boolean} the new active state */
    toggle(handle) {
      const active = this.has(handle);
      if (active) {
        this.remove(handle);
      } else {
        this.add(handle);
      }
      return !active;
    },
  };

  window.Wishlist = Wishlist;

  // Keep other open tabs on the same site in sync too.
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles: readList() } }));
    }
  });

  /**
   * <wishlist-button data-product-handle="...">
   *   <button class="wishlist-button__toggle">...</button>
   * </wishlist-button>
   *
   * Renders in `blocks/_product-card-gallery.liquid` (every product card)
   * and `blocks/buy-buttons.liquid` (the product page), via
   * `snippets/wishlist-button.liquid`.
   */
  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.getAttribute('data-product-handle');
      this.button = this.querySelector('.wishlist-button__toggle');
      this.labelEl = this.querySelector('.wishlist-button__label');
      if (!this.handle || !this.button) return;

      this.syncState();
      this.button.addEventListener('click', this.onClick);
      document.addEventListener('wishlist:change', this.onWishlistChange);
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:change', this.onWishlistChange);
      if (this.button) this.button.removeEventListener('click', this.onClick);
    }

    onClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      window.Wishlist.toggle(this.handle);
    };

    onWishlistChange = () => {
      this.syncState();
    };

    syncState() {
      const active = window.Wishlist.has(this.handle);
      this.setAttribute('data-active', active ? 'true' : 'false');
      this.button.setAttribute('aria-pressed', active ? 'true' : 'false');

      const label = active
        ? this.getAttribute('data-label-remove') || 'Remove from Wishlist'
        : this.getAttribute('data-label-add') || 'Add to Wishlist';

      this.button.setAttribute('aria-label', label);
      if (this.labelEl) this.labelEl.textContent = label;
    }
  }

  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', WishlistButton);
  }

  /**
   * <wishlist-icon> -- header link + count bubble, rendered once in
   * `snippets/header-actions.liquid`.
   */
  class WishlistIcon extends HTMLElement {
    connectedCallback() {
      this.countEl = this.querySelector('[data-wishlist-count]');
      this.bubbleEl = this.querySelector('[data-wishlist-bubble]');
      this.sync();
      document.addEventListener('wishlist:change', this.onChange);
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:change', this.onChange);
    }

    onChange = () => this.sync();

    sync() {
      const count = window.Wishlist.getAll().length;
      if (this.countEl) {
        this.countEl.textContent = count < 100 ? String(count) : '';
        this.countEl.classList.toggle('hidden', count === 0);
      }
      if (this.bubbleEl) {
        const isEmpty = count === 0;
        this.bubbleEl.classList.toggle('hidden', isEmpty);
        this.bubbleEl.classList.toggle('visually-hidden', isEmpty);
      }
      this.classList.toggle('header-actions__wishlist-icon--has-items', count > 0);
    }
  }

  if (!customElements.get('wishlist-icon')) {
    customElements.define('wishlist-icon', WishlistIcon);
  }

  /**
   * Minimal re-implementation of Shopify's `money` filter for the wishlist
   * page, which renders client-side from the Ajax API (`/products/{handle}.js`)
   * rather than server-rendered Liquid.
   */
  function formatMoney(cents, format) {
    const amount = (cents / 100).toFixed(2);
    const [whole, decimal] = amount.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${decimal}`);
  }

  /**
   * <wishlist-grid data-money-format="...">
   *   <ul data-wishlist-grid-list></ul>
   *   <div data-wishlist-grid-empty hidden>...</div>
   * </wishlist-grid>
   *
   * Renders on the standalone Wishlist page (`sections/main-page-wishlist.liquid`).
   * Reads saved handles, fetches each product's public JSON, and renders a
   * simple card with image / title / price / remove / view link.
   */
  class WishlistGrid extends HTMLElement {
    connectedCallback() {
      this.listEl = this.querySelector('[data-wishlist-grid-list]');
      this.emptyEl = this.querySelector('[data-wishlist-grid-empty]');
      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';

      this.render();
      document.addEventListener('wishlist:change', this.onChange);
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:change', this.onChange);
    }

    onChange = () => this.render();

    async render() {
      const handles = window.Wishlist.getAll();

      if (!this.listEl) return;

      if (handles.length === 0) {
        this.listEl.innerHTML = '';
        if (this.emptyEl) this.emptyEl.hidden = false;
        return;
      }

      if (this.emptyEl) this.emptyEl.hidden = true;

      const products = await Promise.all(
        handles.map((handle) =>
          fetch(`/products/${handle}.js`)
            .then((response) => (response.ok ? response.json() : null))
            .catch(() => null)
        )
      );

      // Re-check in case the list changed while these requests were in flight.
      const currentHandles = window.Wishlist.getAll();

      this.listEl.innerHTML = '';

      handles.forEach((handle, index) => {
        if (!currentHandles.includes(handle)) return;

        const product = products[index];
        if (!product) return;

        const li = document.createElement('li');
        li.className = 'wishlist-grid__item';

        const image = product.featured_image || (product.images && product.images[0]) || '';
        const price = formatMoney(product.price, this.moneyFormat);

        li.innerHTML = `
          <div class="wishlist-grid__card">
            <a href="${product.url}" class="wishlist-grid__image-link">
              ${
                image
                  ? `<img src="${image}" alt="" loading="lazy" class="wishlist-grid__image">`
                  : '<div class="wishlist-grid__image wishlist-grid__image--placeholder"></div>'
              }
            </a>
            <div class="wishlist-grid__details">
              <a href="${product.url}" class="wishlist-grid__title">${product.title}</a>
              <span class="wishlist-grid__price">${price}</span>
            </div>
            <button type="button" class="wishlist-grid__remove button-unstyled" data-handle="${handle}" aria-label="Remove from Wishlist">
              <span class="svg-wrapper" aria-hidden="true">
                ${window.WishlistHeartSVG || ''}
              </span>
            </button>
          </div>
        `;

        const removeButton = li.querySelector('.wishlist-grid__remove');
        removeButton.addEventListener('click', () => window.Wishlist.remove(handle));

        this.listEl.appendChild(li);
      });
    }
  }

  if (!customElements.get('wishlist-grid')) {
    customElements.define('wishlist-grid', WishlistGrid);
  }
})();
